import React from 'react';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Loader2 } from 'lucide-react';
import type { ProjectDetails } from '@/types/project';
import type { CostCentre } from '@/types/timesheet';
import type { DataMode } from '@/contexts/DataSourceContext';

export const TimeEntryTab: React.FC<{ project: ProjectDetails; screenMode: DataMode; costCentres: CostCentre[]; isLoadingCostCentres: boolean }> = ({ project, screenMode, costCentres, isLoadingCostCentres }) => {
  return (
    <>
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Available Time Entry Codes</CardTitle>
        </CardHeader>
        <CardContent>
          {screenMode === 'live' ? (
            isLoadingCostCentres ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                <span className="ml-2 text-sm text-muted-foreground">Loading cost centres...</span>
              </div>
            ) : costCentres.length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-4">No cost centres found for this project.</p>
            ) : (
              <div className="space-y-3">
                {costCentres.map((cc) => (
                  <div key={cc.id} className="flex items-center justify-between p-3 border rounded-lg">
                    <div className="flex items-center gap-3">
                      <Badge variant="outline" className="font-mono">
                        {cc.code}
                      </Badge>
                      <div>
                        <p className="text-sm font-medium">{cc.name}</p>
                        {cc.description && (
                          <p className="text-xs text-muted-foreground">{cc.description}</p>
                        )}
                      </div>
                    </div>
                    {cc.status && (
                      <Badge variant="secondary">
                        {cc.status}
                      </Badge>
                    )}
                  </div>
                ))}
              </div>
            )
          ) : (
            <div className="space-y-3">
              {project.timeEntryCodes.map((code) => (
                <div key={code.id} className="flex items-center justify-between p-3 border rounded-lg">
                  <div className="flex items-center gap-3">
                    <Badge variant="outline" className="font-mono">
                      {code.code}
                    </Badge>
                    <div>
                      <p className="text-sm font-medium">{code.name}</p>
                      {code.description && (
                        <p className="text-xs text-muted-foreground">{code.description}</p>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {code.billable && (
                      <Badge variant="secondary" className="bg-success-500/10 text-success-700 dark:text-success-400">
                        Billable
                      </Badge>
                    )}
                    {code.requiresApproval && (
                      <Badge variant="secondary" className="bg-warning-500/10 text-warning-700 dark:text-warning-400">
                        Requires Approval
                      </Badge>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </>
  );
};
